import { ChangeDetectionStrategy, Component, effect, inject, input } from '@angular/core';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ConfigService } from '@fcom/core/services/config/config.service';
import { LanguageService } from '@fcom/ui-translate';
import { RootPaths } from '@fcom/core/constants';
import { SchemaService } from '@fcom/common/services/schema/schema.service';
import { IconPosition } from '@fcom/ui-components/components/icons/enums';
import { LinkSize } from '@fcom/ui-components/components/link/enums';

import { OndMarketingRoutesPath } from '../../interfaces/ond-marketing-offers.interface';

export type OndMarketingDisambiguationLinksMode = 'list' | 'ribbon';

interface DisambiguationLink {
  href: string;
  translationKey: string;
}

const LIST_HEADING_KEY = 'ondMarketing.heading.flights';

@Component({
  selector: 'fin-ond-marketing-disambiguation-links',
  templateUrl: './ond-marketing-disambiguation-links.component.html',
  styleUrls: ['./ond-marketing-disambiguation-links.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class OndMarketingDisambiguationLinksComponent {
  private languageService = inject(LanguageService);
  private configService = inject(ConfigService);
  private schemaService = inject(SchemaService);

  readonly mode = input<OndMarketingDisambiguationLinksMode>('ribbon');

  readonly IconPosition = IconPosition;
  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly LinkSize = LinkSize;

  private readonly lang = `/${this.languageService.langValue}/`;
  private readonly linkTranslationKeys = ['from', 'fromCountry', 'cityToCity', 'countryToCountry'];

  readonly links: DisambiguationLink[] = Object.values(OndMarketingRoutesPath).map((path, index) => {
    return {
      href: `${this.lang}${RootPaths.OND_MARKETING_LANDING_ROOT}/${path}`,
      translationKey: `ondMarketing.ribbon.${this.linkTranslationKeys[index]}`,
    };
  });

  private readonly schemaLabels = this.languageService.translateMultipleSignal([
    LIST_HEADING_KEY,
    ...this.links.map((link) => link.translationKey),
  ]);

  constructor() {
    let added = false;
    effect(() => {
      const labels = this.schemaLabels();
      if (added || this.mode() !== 'list' || !labels) {
        return;
      }
      added = true;

      const baseUrl = this.configService.cfg.baseUrl;
      const [name, ...linkLabels] = labels;
      const items = this.links.map((link, index) => {
        return { url: `${baseUrl}${link.href}`, name: linkLabels[index] };
      });
      this.schemaService.addRouteListSchema({ name, items });
    });
  }
}
